import React from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import StandardizedChip from '../../../common/StandardizedChip';
import {
  CompareArrows as CompareIcon,
  Star as StarIcon,
} from '@mui/icons-material';
import {
  formatPercentage,
  formatCurrency,
  formatDate,
  getSuccessRateColor,
  getRatingColor,
} from './utils';

/**
 * VersionComparisonTable Component
 *
 * Displays a comparison table of all template versions.
 *
 * @param {Object} props
 * @param {Array} props.versionAnalytics - Array of version analytics objects
 * @param {Object|null} props.selectedVersion - Currently selected version
 * @param {Function} props.onVersionSelect - Handler for version selection
 */
function VersionComparisonTable({
  versionAnalytics,
  selectedVersion,
  onVersionSelect,
}) {
  if (
    !versionAnalytics ||
    !Array.isArray(versionAnalytics) ||
    versionAnalytics.length === 0
  ) {
    return null;
  }

  const sortedVersions = [...versionAnalytics].sort(
    (a, b) => (b.version_number || 0) - (a.version_number || 0)
  );
  const latestVersionNumber = sortedVersions[0]?.version_number;

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box display="flex" alignItems="center" mb={2}>
          <CompareIcon color="primary" sx={{ mr: 1 }} />
          <Typography variant="h6">Version Comparison</Typography>
          <StandardizedChip
            label={`${versionAnalytics.length} Version${
              versionAnalytics.length > 1 ? 's' : ''
            }`}
            color="primary"
            sx={{ ml: 2 }}
          />
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Click on a version row to view detailed analytics below
        </Typography>
        <TableContainer
          component={Paper}
          variant="outlined"
          sx={{ maxHeight: '400px', overflow: 'auto' }}
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    fontWeight: 'bold',
                    backgroundColor: 'background.paper',
                    position: 'sticky',
                    left: 0,
                    zIndex: 3,
                    minWidth: '100px',
                  }}
                >
                  Version
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 'bold',
                    backgroundColor: 'background.paper',
                  }}
                >
                  Status
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 'bold',
                    backgroundColor: 'background.paper',
                  }}
                >
                  Provider
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 'bold',
                    backgroundColor: 'background.paper',
                  }}
                >
                  Model
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                  Executions
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                  Success Rate
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                  Avg Time
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                  Total Cost
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                  Avg Rating
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                  Feedback
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                  Last Activity
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedVersions.map((version, index) => {
                const isLatest = version.version_number === latestVersionNumber;
                const isSelected =
                  selectedVersion &&
                  selectedVersion.version_number === version.version_number;

                return (
                  <TableRow
                    key={version.version_number || index}
                    onClick={() => onVersionSelect(version)}
                    sx={{
                      cursor: 'pointer',
                      backgroundColor: isSelected
                        ? 'action.selected'
                        : 'transparent',
                      '&:hover': {
                        backgroundColor: 'action.hover',
                      },
                    }}
                  >
                    <TableCell
                      sx={{
                        position: 'sticky',
                        left: 0,
                        backgroundColor: isSelected
                          ? 'action.selected'
                          : 'background.paper',
                        zIndex: 2,
                      }}
                    >
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="body2" fontWeight="medium">
                          v{version.version_number || 'N/A'}
                        </Typography>
                        {isLatest && (
                          <StandardizedChip
                            label="Latest"
                            color="primary"
                            height={20}
                            width={60}
                            sx={{
                              fontSize: '0.7rem',
                            }}
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <StandardizedChip
                        label={
                          version.version_status?.replace(
                            'PromptVersion.',
                            ''
                          ) || 'N/A'
                        }
                        color={
                          version.version_status?.includes('ACTIVE')
                            ? 'success'
                            : 'default'
                        }
                        width={96}
                      />
                    </TableCell>
                    <TableCell>
                      <StandardizedChip
                        label={version.provider || 'N/A'}
                        color="default"
                        variant="outlined"
                        width={96}
                        sx={{
                          bgcolor: 'grey.100',
                          color: 'text.primary',
                          borderColor: 'grey.300',
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap>
                        {version.model || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">
                        {version.total_executions || 0}
                      </Typography>
                      {version.successful_executions !== undefined &&
                        version.failed_executions !== undefined && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                          >
                            {version.successful_executions} /{' '}
                            {version.failed_executions}
                          </Typography>
                        )}
                    </TableCell>
                    <TableCell align="right">
                      <StandardizedChip
                        label={formatPercentage(
                          version.success_rate_percentage
                        )}
                        color={getSuccessRateColor(
                          version.success_rate_percentage
                        )}
                        width={96}
                        tooltipTitle={`Successful: ${
                          version.successful_executions || 0
                        }, Failed: ${version.failed_executions || 0}`}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">
                        {version.avg_execution_time
                          ? `${version.avg_execution_time.toFixed(2)}s`
                          : 'N/A'}
                      </Typography>
                      {version.min_execution_time !== undefined &&
                        version.max_execution_time !== undefined && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                          >
                            {version.min_execution_time.toFixed(2)}s -{' '}
                            {version.max_execution_time.toFixed(2)}s
                          </Typography>
                        )}
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">
                        {formatCurrency(version.total_cost)}
                      </Typography>
                      {version.avg_cost !== undefined && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                        >
                          {formatCurrency(version.avg_cost)}/exec
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {version.avg_rating ? (
                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent="flex-end"
                          gap={0.5}
                        >
                          <StarIcon
                            sx={{
                              fontSize: '1rem',
                              color:
                                getRatingColor(version.avg_rating) === 'success'
                                  ? 'warning.main'
                                  : getRatingColor(version.avg_rating) ===
                                      'warning'
                                    ? 'warning.main'
                                    : 'text.secondary',
                            }}
                          />
                          <Typography variant="body2">
                            {version.avg_rating.toFixed(2)}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          N/A
                        </Typography>
                      )}
                      {version.min_rating !== undefined &&
                        version.max_rating !== undefined && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                          >
                            {version.min_rating} - {version.max_rating}
                          </Typography>
                        )}
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">
                        {version.total_feedback_count || 0}
                      </Typography>
                      {version.feedback_coverage_percentage !== undefined && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                        >
                          {formatPercentage(
                            version.feedback_coverage_percentage
                          )}{' '}
                          coverage
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" noWrap>
                        {version.last_activity_at
                          ? formatDate(version.last_activity_at)
                          : 'N/A'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
}

export default VersionComparisonTable;
